/**
 * API client para gestión de plantillas email + audit log.
 *
 * Reusa los tipos `DocType` y helpers de api-pdf-templates ya que son
 * los mismos 3 doc_types (consistencia con backend).
 */

import { api } from './api'
import type { DocType, ListResponse } from './api-pdf-templates'

export type { DocType, ListResponse } from './api-pdf-templates'
export { DOC_TYPES, DOC_TYPE_LABELS } from './api-pdf-templates'

// ─── Tipos ──────────────────────────────────────────────────────────

export interface EmailTemplate {
  id?: string
  tax_client_id?: string
  doc_type: DocType
  subject_template: string
  html_template: string
  text_template: string
  version: number
  is_active?: boolean
  created_by_user_id?: string
  updated_by_user_id?: string
  created_at?: string | null
  updated_at?: string | null
  source: 'db' | 'default'
}

export interface EmailPreview {
  subject: string
  html: string
  text: string
}

export interface EmailLog {
  id: string
  tax_client_id: string
  document_id: string
  doc_type: DocType
  doc_number: string
  recipient_address: string
  cc_addresses?: string
  bcc_addresses?: string
  from_address: string
  subject: string
  status: 'pending' | 'sent' | 'failed' | 'bounced'
  smtp_response?: string
  attempt_count: number
  asynq_task_id?: string
  queued_at: string
  sent_at?: string
  failed_at?: string
  triggered_by_user_id?: string
}

export interface EmailLogListResponse {
  items: EmailLog[]
  total: number
  limit: number
  offset: number
}

export interface EmailLogFilter {
  taxClientId?: string
  documentId?: string
  status?: 'pending' | 'sent' | 'failed' | 'bounced' | ''
  from?: string // RFC 3339
  to?: string
  limit?: number
  offset?: number
}

// ─── API methods ────────────────────────────────────────────────────

export const emailTemplatesApi = {
  list: (clientId: string) =>
    api
      .get<ListResponse<EmailTemplate>>(
        `/api/v1/admin/tax-clients/${clientId}/email-templates`,
      )
      .then((r) => r.data),

  get: (clientId: string, docType: DocType) =>
    api
      .get<EmailTemplate>(
        `/api/v1/admin/tax-clients/${clientId}/email-templates/${docType}`,
      )
      .then((r) => r.data),

  upsert: (
    clientId: string,
    docType: DocType,
    body: { subject_template: string; html_template: string; text_template: string },
  ) =>
    api
      .put<EmailTemplate>(
        `/api/v1/admin/tax-clients/${clientId}/email-templates/${docType}`,
        body,
      )
      .then((r) => r.data),

  delete: (clientId: string, docType: DocType) =>
    api
      .delete<void>(
        `/api/v1/admin/tax-clients/${clientId}/email-templates/${docType}`,
      )
      .then((r) => r.data),

  /**
   * Renderiza preview con sample data. Si se pasa body con los 3
   * sub-templates, renderiza esos (preview de edición sin guardar).
   */
  preview: (
    clientId: string,
    docType: DocType,
    body?: { subject_template: string; html_template: string; text_template: string },
  ) =>
    api
      .post<EmailPreview>(
        `/api/v1/admin/tax-clients/${clientId}/email-templates/${docType}/preview`,
        body,
      )
      .then((r) => r.data),

  getDefault: (docType: DocType) =>
    api
      .get<EmailTemplate>(`/api/v1/admin/email-templates/defaults/${docType}`)
      .then((r) => r.data),
}

export const emailLogApi = {
  list: (filter: EmailLogFilter = {}) => {
    const params = new URLSearchParams()
    if (filter.taxClientId) params.set('tax_client_id', filter.taxClientId)
    if (filter.documentId) params.set('document_id', filter.documentId)
    if (filter.status) params.set('status', filter.status)
    if (filter.from) params.set('from', filter.from)
    if (filter.to) params.set('to', filter.to)
    if (filter.limit !== undefined) params.set('limit', String(filter.limit))
    if (filter.offset !== undefined) params.set('offset', String(filter.offset))
    const qs = params.toString()
    const url = qs ? `/api/v1/admin/email-log?${qs}` : '/api/v1/admin/email-log'
    return api.get<EmailLogListResponse>(url).then((r) => r.data)
  },

  resend: (logId: string) =>
    api
      .post<{ message?: string }>(`/api/v1/admin/email-log/${logId}/resend`)
      .then((r) => r.data),
}
